import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import FormButton from 'components/form-components/FormButton';
import MultiCheckBoxSelect from 'components/form-components/MultiCheckBoxSelect';
import SingleCheckBoxField from 'components/form-components/SingleCheckBoxField';

import editGreyIcon from 'assets/images/icons/edit-grey.svg';
import chevronGreyIcon from 'assets/images/icons/chevron-grey.svg';

import { CallAPI } from 'actions/General';
import { generateTextWithSuffix } from 'components/utils';
import { cloneDeep } from 'lodash';
import DropdownWrapper from 'components/common-components/DropdownWrapper';

const TaskDetailPermission = ({ permissionsList, setPermissionsList }) => {
  const { t } = useTranslation();

  const [values, setValues] = useState({
    allCompanyFields: false,
    noCompanyFields: false,
    companyFields: {},
  });

  const [selections, setSelections] = useState({});
  const [fieldOptions, setFieldOptions] = useState({});
  const [dataSourceValuesMap, setDataSourceValuesMap] = useState({});

  const handleToggleChange = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleCompanyFieldToggle = (fieldId, checked) => {
    setValues((prev) => ({
      ...prev,
      companyFields: { ...prev.companyFields, [fieldId]: checked },
    }));
  };

  const handleSelectionChange = (fieldId, selectedIds) => {
    setSelections((prev) => ({
      ...prev,
      [fieldId]: selectedIds,
    }));
  };

  const fetchCompanyFields = async () => {
    const { status, data } = await CallAPI('GET_ALL_COMPANY_FIELDS', {});
    if (!status || !data?.length) return;

    const fieldMap = {};
    const payload = { items: [] };

    for (const item of data) {
      fieldMap[item._id] = {
        label: item.fieldTitle,
        dataSourceId: item.dataSource._id,
        dataSourceColumn: item.dataSourceColumn,
      };
      payload.items.push({
        dataSourceId: item.dataSource._id,
        columnId: item.dataSourceColumn,
      });
    }

    const entriesResponse = await CallAPI('GET_MULTIPLE_DATASOURCE_ENTRIES', payload);
    if (entriesResponse.status) {
      const valueLookup = {};
      for (const fieldId in fieldMap) {
        const { dataSourceId, dataSourceColumn } = fieldMap[fieldId];
        const ds = entriesResponse.data.find((d) => d.dataSourceId === dataSourceId);
        valueLookup[fieldId] = (ds?.entries || [])
          .filter((e) => e.entryValue?.keyValue && e.entryValue?.columnId === dataSourceColumn)
          .map((e) => ({ value: e.rowId, displayValue: e.entryValue.keyValue }));
      }
      setDataSourceValuesMap(valueLookup);
    }

    setFieldOptions(fieldMap);
  };

  useEffect(() => {
    const tasks = permissionsList?.field_work?.moduleLevels?.tasks;
    const perms = tasks?.companyFieldPermission;
    if (!perms) return;

    const companyFields = {};
    const sels = {};

    (perms.companyFieldPermissionList || []).forEach(({ companyFieldId, dataSourceRowIds }) => {
      companyFields[companyFieldId] = true;
      sels[companyFieldId] = dataSourceRowIds || [];
    });

    setValues({
      allCompanyFields: perms.allCompanyFields || false,
      noCompanyFields: perms.noCompanyFields || false,
      companyFields,
    });
    setSelections(sels);
  }, []);

  useEffect(() => {
    setPermissionsList((prev) => {
      const updated = cloneDeep(prev);
      const tasks = updated.field_work?.moduleLevels?.tasks;
      if (!tasks) return prev;

      const list = [];
      for (const fieldId in values.companyFields) {
        if (values.companyFields[fieldId] && selections[fieldId]?.length > 0) {
          list.push({
            companyFieldId: fieldId,
            dataSourceRowIds: selections[fieldId],
          });
        }
      }

      tasks.companyFieldPermission = {
        allCompanyFields: values.allCompanyFields,
        noCompanyFields: values.noCompanyFields,
        companyFieldPermissionList: list,
      };
      return updated;
    });
  }, [values, selections, setPermissionsList]);

  useEffect(() => {
    fetchCompanyFields();
  }, []);

  const getDisplayValue = useCallback(
    (options, selected, fallback) => {
      if (!options?.length) return fallback;
      const displayValues = selected
        .map((id) => options.find((o) => o.value === id)?.displayValue)
        .filter(Boolean);
      return generateTextWithSuffix(displayValues, 1, (count) => ` +${count}`, fallback, t);
    },
    [t],
  );

  const isDetailsDisabled = values.allCompanyFields;

  return (
    <div className='d-flex gap-2 w-100'>
      <span className='flex-grow-0' style={{ width: '30%' }} />
      <div className='p-3 flex-grow-1 theme-background-white-5 rounded-sm'>
        <div className='mb-2'>{t('text_view_task')}</div>

        <div className='d-flex align-items-center gap-3 mb-3'>
          <SingleCheckBoxField
            name='taskAllCompanyFields'
            text={<span>{t('text_all')}</span>}
            value={values.allCompanyFields}
            handleChange={(_, value) => handleToggleChange('allCompanyFields', value)}
            shrink
          />

          <DropdownWrapper
            toggleClasses={`d-flex justify-content-between rounded-sm cursor-pointer ${
              isDetailsDisabled ? 'opacity-50 pointer-events-none' : ''
            }`}
            toggleComponent={
              <div className='d-flex gap-2 px-2 py-1 theme-background-white-1 rounded-sm cursor-pointer'>
                <span>{t('button_details')}</span>
                <img src={editGreyIcon} />
              </div>
            }
            dropdownContent={({ handleCloseDropdown }) => (
              <div className='px-3 py-3 user-select-none' style={{ width: '20rem' }}>
                <div className='row mb-2 fw-bold'>
                  <span className='col-6'>{t('table_header_company_fields')}</span>
                  <span className='col-6'>{t('text_value')}</span>
                </div>

                {Object.entries(fieldOptions).map(([fieldId, field]) => {
                  const isChecked = values.companyFields[fieldId] || false;
                  const selected = selections[fieldId] || [];
                  const options = dataSourceValuesMap[fieldId] || [];

                  return (
                    <div key={fieldId} className='row mb-2 align-items-center'>
                      <div className='col-6'>
                        <SingleCheckBoxField
                          name={`field-${fieldId}`}
                          text={<span>{field.label}</span>}
                          value={isChecked}
                          handleChange={(name, val) => handleCompanyFieldToggle(fieldId, val)}
                          shrink
                        />
                      </div>
                      <div className='col-6'>
                        <MultiCheckBoxSelect
                          disabled={!isChecked}
                          options={options}
                          value={selected}
                          handleChange={(name, val) => handleSelectionChange(fieldId, val)}
                          name={fieldId}
                          toggleClasses={`px-2 py-1 border-grey-1-h-1 align-items-center ${
                            !isChecked ? 'opacity-50 pointer-events-none' : ''
                          }`}
                          toggleComponent={
                            <>
                              <label className='theme-size-0_9 cursor-pointer'>
                                {t(getDisplayValue(options, selected, 'select_value'))}
                              </label>
                              <img src={chevronGreyIcon} className='rotate-180' alt='expand' />
                            </>
                          }
                          fullWidth
                        />
                      </div>
                    </div>
                  );
                })}

                <FormButton
                  text='button_close'
                  variant='green-2'
                  shrink
                  classes='w-100'
                  onClick={() => handleCloseDropdown()}
                />
              </div>
            )}
            mountTarget={'inline'}
          />
        </div>

        <SingleCheckBoxField
          name='taskNoCompanyFields'
          text={<span>{t('text_no_company_field')}</span>}
          value={values.noCompanyFields}
          handleChange={(_, value) => handleToggleChange('noCompanyFields', value)}
          shrink
        />
      </div>
    </div>
  );
};

TaskDetailPermission.propTypes = {
  permissionsList: PropTypes.object.isRequired,
  setPermissionsList: PropTypes.func.isRequired,
};

export default TaskDetailPermission;
